import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';

// API Base URL - use relative /api in production (nginx proxies to backend)
// Can be overridden with REACT_APP_API_URL at build time
// Local default uses HTTP :5223 (HTTPS :7123 only if the API is started with the https profile)
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5223/api');

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Share identical in-flight GET requests so StrictMode remounts and
 * concurrent callers (parent + child) hit the network once.
 */
const inflightGets = new Map<string, Promise<AxiosResponse>>();

const buildGetDedupeKey = (config: AxiosRequestConfig): string | null => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') {
    return null;
  }
  // Don't dedupe requests that the caller intends to cancel independently
  if (config.signal) {
    return null;
  }
  const url = config.url || '';
  const params =
    config.params === undefined
      ? ''
      : typeof config.params === 'string'
        ? config.params
        : JSON.stringify(config.params);
  const token =
    (config.headers as Record<string, string> | undefined)?.Authorization ||
    localStorage.getItem('token') ||
    '';
  return `${method}|${url}|${params}|${token}`;
};

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const originalRequest = api.request.bind(api);
api.request = function dedupedRequest<T = any, R = AxiosResponse<T>, D = any>(
  config: AxiosRequestConfig<D>
): Promise<R> {
  const key = buildGetDedupeKey(config);
  if (!key) {
    return originalRequest(config);
  }

  const existing = inflightGets.get(key);
  if (existing) {
    return existing as Promise<R>;
  }

  const promise = originalRequest(config).finally(() => {
    if (inflightGets.get(key) === promise) {
      inflightGets.delete(key);
    }
  }) as Promise<AxiosResponse>;

  inflightGets.set(key, promise);
  return promise as Promise<R>;
};
// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Do not run refresh logic for login or refresh endpoint — let the caller show the error (e.g. "Invalid email or password")
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/refresh');

    // If error is 401 and we haven't tried to refresh yet (and this isn't a login/refresh request)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers && token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token, logout user
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        processQueue(error, null);
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { token: newToken, refreshToken: newRefreshToken } =
          response.data;
        localStorage.setItem('token', newToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      const data = error.response.data as any;

      switch (status) {
        case 403:
          // Forbidden - user doesn't have permission
          return Promise.reject(
            new Error(
              data?.message ||
                'You do not have permission to perform this action.'
            )
          );
        case 404:
          return Promise.reject(
            new Error(data?.message || 'Resource not found.')
          );
        case 500:
          return Promise.reject(
            new Error(data?.message || 'Server error. Please try again later.')
          );
        default:
          return Promise.reject(
            new Error(data?.message || 'An error occurred. Please try again.')
          );
      }
    } else if (error.request) {
      // Request was made but no response received
      return Promise.reject(
        new Error('Network error. Please check your connection and try again.')
      );
    } else {
      // Something else happened
      return Promise.reject(error);
    }
  }
);

export default api;
